/**
 * One hanke in the Etusivu "Hankkeiden tilanne" grid: completion ratio, a
 * segmented progress bar, the three counts that decide whether the hanke needs
 * attention, and the next task coming due.
 *
 * The progress bar's green segment is valmis, yellow is kesken, and the
 * remaining gray track is aloittamatta.
 */

import { Card } from "@/components/ui/card";
import { SchedulingChangesDot } from "@/components/common/SchedulingChangesDot";
import { formatDateFi } from "@/lib/format";
import { ProjectStatus } from "@/types";
import { cn } from "@/lib/utils";

const STATUS_LABELS: Record<string, string> = {
  [ProjectStatus.TENDERING]: "Tarjousvaihe",
  [ProjectStatus.LOST]: "Hävitty",
  [ProjectStatus.PLANNING]: "Suunnittelu",
  [ProjectStatus.IN_PROGRESS]: "Käynnissä",
  [ProjectStatus.COMPLETED]: "Luovutettu",
  [ProjectStatus.WARRANTY]: "Takuuvaihe",
  [ProjectStatus.ARCHIVED]: "Arkistoitu",
};

export interface ProjectHealth {
  id: string;
  name: string;
  status: string;
  total: number;
  completed: number;
  inProgress: number;
  late: number;
  dueThisWeek: number;
  next: { title: string; dueDate: string } | null;
}

export function ProjectHealthCard({
  health,
  onClick,
  hasSchedulingChanges = false,
}: {
  health: ProjectHealth;
  onClick: () => void;
  hasSchedulingChanges?: boolean;
}) {
  const { total, completed, inProgress, late, dueThisWeek } = health;
  const pct = total ? Math.round((completed / total) * 100) : 0;
  const completedWidth = total ? (completed / total) * 100 : 0;
  const inProgressWidth = total ? (inProgress / total) * 100 : 0;
  const label = STATUS_LABELS[health.status] ?? health.status;

  return (
    <Card
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onClick();
        }
      }}
      className="cursor-pointer p-5 transition-colors hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2">
          <h3 className="min-w-0 truncate text-[17px] font-semibold leading-6" title={health.name}>
            {health.name}
          </h3>
          {hasSchedulingChanges && (
            <SchedulingChangesDot title="Sitedrive-muutoksia saatavilla" />
          )}
        </div>
        <span
          className={cn(
            "shrink-0 rounded-full px-2.5 py-1 text-xs font-medium leading-4",
            health.status === ProjectStatus.IN_PROGRESS
              ? "bg-green-50 text-green-700"
              : "bg-muted text-muted-foreground",
          )}
        >
          {label}
        </span>
      </div>

      <div className="mt-4 flex items-baseline justify-between gap-3">
        <span className="text-sm text-muted-foreground">
          {total > 0
            ? `${completed} / ${total} tehtävää valmiina`
            : "Ei tehtäviä"}
        </span>
        {total > 0 && (
          <span className="text-sm font-medium tabular-nums text-muted-foreground">
            {pct} %
          </span>
        )}
      </div>

      <div className="mt-2 flex h-2 w-full overflow-hidden rounded-full bg-slate-200">
        {completedWidth > 0 && (
          <div className="h-full bg-green-400" style={{ width: `${completedWidth}%` }} />
        )}
        {inProgressWidth > 0 && (
          <div className="h-full bg-yellow-400" style={{ width: `${inProgressWidth}%` }} />
        )}
      </div>

      <div className="mt-4 flex flex-wrap items-baseline gap-x-3 gap-y-1 text-sm">
        {late > 0 ? (
          <span className="font-semibold text-red-600">{late} myöhässä</span>
        ) : (
          <span className="text-green-700">Aikataulussa</span>
        )}
        <span className="text-border">·</span>
        <span className="text-muted-foreground">{inProgress} kesken</span>
        <span className="text-border">·</span>
        <span className="text-muted-foreground">{dueThisWeek} erääntyy tällä viikolla</span>
      </div>

      <div className="mt-4 border-t border-border pt-4 text-sm text-muted-foreground">
        {health.next ? (
          <span className="block truncate" title={health.next.title}>
            Seuraavaksi: {health.next.title} · {formatDateFi(health.next.dueDate)}
          </span>
        ) : (
          <span>Ei tulevia määräaikoja</span>
        )}
      </div>
    </Card>
  );
}
